import { HStack, Image, Menu, Portal, Box, Text, VStack, Icon, IconButton } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import AvatarPlaceholder from '@/assets/images/avatar_placeholder.svg';
import { useAuth } from '@/auth/AuthContext';
import { usePageHeader } from '@/components/layout/PageHeaderContext';
import LanguageSelector from "@/components/ui/menu/LanguageSelector";
import { HiBars3 } from "react-icons/hi2";

const TopBar = ({ onSidebarOpen }) => {
    const { t } = useTranslation("common");
    const { currentUser, logout } = useAuth();
    const header = usePageHeader();

    const displayName = currentUser
        ? `${currentUser.firstName} ${currentUser.lastName}`
        : '';

    const initials = currentUser
        ? `${currentUser.firstName?.[0] ?? ''}${currentUser.lastName?.[0] ?? ''}`.toUpperCase()
        : '';

    return (
        <HStack
            bg="white"
            borderBottom="1px solid"
            borderColor="#E7E4F0"
            minH={20}
            w={'full'}
            justifyContent={'space-between'}
            alignItems={'center'}
            px={{ base: 3, md: 6 }}
            position={{ base: 'fixed', md: 'static' }}
            top={0}
            zIndex={10}
        >
            <HStack gap={2}>
                <IconButton
                    display={{ base: 'flex', md: 'none' }}
                    variant="ghost"
                    color="#65616F"
                    aria-label="Menu"
                    onClick={onSidebarOpen}
                >
                    <HiBars3 />
                </IconButton>
                <VStack alignItems="flex-start" gap={0}>
                    <HStack alignItems={'center'}>
                        {header.icon &&
                            <Icon size={'md'} color="var(--brand-from, #6D28D9)">
                                {header.icon}
                            </Icon>
                        }
                        {header.title && (
                            <Text
                                fontWeight={700}
                                fontSize="17px"
                                bgGradient="to-r"
                                gradientFrom="var(--brand-from, #6D28D9)"
                                gradientTo="var(--brand-to, #9D00FF)"
                                bgClip="text"
                                color="transparent"
                            >
                                {header.title}
                            </Text>
                        )}
                    </HStack>
                    {header.subtitle && <Text color={"#9A96A6"} fontSize="sm">{header.subtitle}</Text>}
                </VStack>
            </HStack>

            <HStack gap={3}>
                <Box display={{ base: 'none', md: 'block' }}>
                    <LanguageSelector />
                </Box>

                <Menu.Root>
                    <Menu.Trigger asChild>
                        <HStack
                            as="button"
                            color={"#17151F"}
                            columnGap={3}
                            transition="background 0.2s ease-in-out"
                            _hover={{ bg: "#F6F6FB", textDecoration: 'unset' }}
                            py={2}
                            pl={2}
                            pr={{ base: 2, md: 4 }}
                            borderRadius={32}
                            cursor="pointer"
                        >
                            {currentUser?.avatar ? (
                                <Image src={currentUser.avatar} w={8} borderRadius="full" />
                            ) : (
                                <Box
                                    w={8}
                                    h={8}
                                    borderRadius="full"
                                    bg="purple.500"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <Text fontSize={12} color="white" fontWeight="bold">{initials ||
                                        <Image src={AvatarPlaceholder} w={8} />}</Text>
                                </Box>
                            )}
                            <Text fontSize={16} display={{ base: 'none', md: 'block' }}>{displayName}</Text>
                        </HStack>
                    </Menu.Trigger>
                    <Portal>
                        <Menu.Positioner>
                            <Menu.Content bg="white" borderColor="#E7E4F0" boxShadow="0 8px 24px rgba(23,21,31,0.12)">
                                <Menu.Item value="logout" onClick={logout} color="#17151F" _hover={{ bg: "#F6F6FB" }}>
                                    {t("actions.signOut")}
                                </Menu.Item>
                            </Menu.Content>
                        </Menu.Positioner>
                    </Portal>
                </Menu.Root>
            </HStack>
        </HStack>
    );
};

export default TopBar;
